import useLocalStorage, {writeStorage} from '@rehooks/local-storage';
import useInterval from '@use-it/interval';
import {APIResponseStatus, LocalStorageKeys} from 'common/types';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {usePrevious} from 'react-use';
import {MarketActivityApi} from 'services/MarketActivityApi';
import {
  getMarketActivityList,
  getSummaryStat,
  isMarketListLoading,
  setMarketActivityList,
  setMarketActivityListAppend,
  setMarketListLoading,
  setMarketTotalCount,
  setSummaryStats,
} from 'store/market-activity/reducer';
import {
  MarketActivityEnum,
  MarketCityInfo,
  MarketFilter,
  MarketPagination, MarketRoleEnum,
} from 'store/market-activity/types';

export const useStatSummary = (filter: MarketFilter) => {
  const dispatch = useDispatch();
  const api = new MarketActivityApi();
  const stats = useSelector(getSummaryStat);

  useEffect(() => {
    api.fetchSummaryStats(filter)
      .then(response => {
        if (response.status === APIResponseStatus.Ok) {
          dispatch(setSummaryStats(response.data));
        }
      })
      .catch(error => {
        console.debug(error);
      });
  }, [filter]);

  return stats;
};

export const useActivityList = (filter: MarketFilter, pager: MarketPagination, timer: number) => {
  const dispatch = useDispatch();
  const api = new MarketActivityApi();
  const list = useSelector(getMarketActivityList);

  const prevTimer = usePrevious(timer);
  const prevFilter = usePrevious(filter);
  const isLoading = useSelector(isMarketListLoading);
  const [firstVisit, setFirstVisit] = useMarketFirstVisit();

  useEffect(() => {
    dispatch(setMarketListLoading(true));
    // Reset to first page on filter change to avoid empty results
    const filterChanged = prevFilter !== undefined && prevFilter !== filter;
    const timerChanged = prevTimer !== undefined && prevTimer !== timer;
    if (filterChanged || timerChanged || firstVisit) {
      pager.currentPage = 0;
    }

    if (firstVisit) {
      filter.activity = MarketActivityEnum.All;
      filter.role = MarketRoleEnum.All;
    }

    if (!isLoading) {
      api.fetchActivityList(filter, pager)
        .then(response => {
          if (response.status === APIResponseStatus.Ok) {
            if (filterChanged || timerChanged || firstVisit) {
              dispatch(setMarketActivityList(response.data.list));
            } else {
              dispatch(setMarketActivityListAppend(response.data.list));
            }
            dispatch(setMarketTotalCount(response.data.count));
          }
        })
        .catch(error => {
          console.debug(error);
        })
        .finally(() => {
          dispatch(setMarketListLoading(false));
          setFirstVisit(false);
        });
    }
  }, [filter, pager, timer]);

  return list;
};

export const useAddToPartner = (): [
  boolean,
  (userId: number, uuid: string) => void,
  number,
] => {
  const [isLoading, setLoading] = useState(false);
  const [status, setStatus] = useState(0);
  const api = new MarketActivityApi();

  const handler = (userId: number, uuid: string) => {
    setLoading(true);
    api.addToPartner(userId, uuid)
      .then(response => {
        if (response.status === APIResponseStatus.Ok) {
          setStatus(1);
        }
      })
      .catch(error => {
        console.debug(error);
        setStatus(-1);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return [
    isLoading,
    handler,
    status
  ];
};

export const useConfirmPartner = (): [
  boolean,
  (userId: number, uuid: string) => void,
  number,
] => {
  const [isLoading, setLoading] = useState(false);
  const [status, setStatus] = useState(0);
  const api = new MarketActivityApi();

  const handler = (userId: number, uuid: string) => {
    setLoading(true);
    api.confirmPartner(userId, uuid)
      .then(response => {
        if (response.status === APIResponseStatus.Ok) {
          setStatus(1);
        }
      })
      .catch(error => {
        console.debug(error);
        setStatus(-1);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return [
    isLoading,
    handler,
    status,
  ];
};

export const useCityInfo = (cityId?: number) => {
  const api = new MarketActivityApi();
  const [cityInfo, setCityInfo] = useState<null|MarketCityInfo>(null);

  useEffect(() => {
    if (cityId) {
      api.fetchCityInfo(cityId)
        .then(response => {
          if (response.status === APIResponseStatus.Ok) {
            setCityInfo(response.data);
          }
        })
        .catch(error => {
          console.debug(error);
        })
        .finally(() => {

        });
    }
  }, [cityId]);

  return cityInfo;
};

export const scrollTop = () => {
  window.scrollTo(0, 0);
};

export const useMarketTimer = (active: boolean) => {
  const [seconds, setSeconds] = useState<number>(0);

  useInterval(
    () => {
      setSeconds(seconds + 1);
    },
    active ? 1000 * 60 : null,
  );

  return seconds;
};

// Handle activity list reload after change page (router url) without reloading
export const useMarketFirstVisit = (): [
  boolean,
  (state: boolean) => void
] => {
  const [marketFirstVisit] = useLocalStorage<boolean>(LocalStorageKeys.MarketFirstVisit, true);

  const setFirstVisit = (status: boolean) => {
    writeStorage(LocalStorageKeys.MarketFirstVisit, status);
  };

  return [
    !!marketFirstVisit,
    setFirstVisit
  ];
};
